<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import WikiText from '@/components/wikitext/WikiText.vue'
import WikiTextEditor from '@/components/editor/WikiTextEditor.vue'

const PLACEHOLDER = 'How could you implement this strategy?'
const READ_ONLY_HINT = 'Only the Owner of this Shared Folder can make edits.'
// Vue 2's Bootstrap collapse timing; children clip only while animating so focus rings inside stay whole
const COLLAPSE_TRANSITION = 'transition-[grid-template-rows] [transition-duration:350ms] [transition-timing-function:ease] motion-reduce:[transition-duration:0s] [&>*]:overflow-hidden'

const props = withDefaults(defineProps<{
  notes?: string
  studentName: string
  userName: string
  collapsible?: boolean
  editable?: boolean
  readOnly?: boolean
}>(), {
  notes: '',
  collapsible: false,
  editable: false,
  readOnly: false
})

const emit = defineEmits<{ notesEdited: [notes: string] }>()

const headingId = useId()
const editorRef = ref<InstanceType<typeof WikiTextEditor> | null>(null)
const showNotes = ref(true)
const editedNotes = ref(props.notes)

const heading = computed(() => props.editable ? `Notes for ${props.studentName}` : `${props.userName}'s Notes`)

defineExpose({ focusEditButton: () => editorRef.value?.focusControls() })

function updateNotes(notes: string) {
  editedNotes.value = notes
  emit('notesEdited', notes)
}
</script>

<template>
  <section class="bg-yellow-100 px-4 py-2.5 shadow-lib-card">
    <h2 :id="headingId" class="mb-2.5 text-display-sm font-bold leading-[1.1] text-blue-400">
      <button
        v-if="collapsible"
        type="button"
        class="group flex w-full items-center text-left"
        :aria-expanded="showNotes"
        @click="showNotes = !showNotes"
      >
        <span class="material-icons-round mr-1" aria-hidden="true">push_pin</span>
        <span class="fs-exclude group-hover:underline">{{ heading }}</span>
        <span class="material-icons-round ml-auto" aria-hidden="true">{{ showNotes ? 'expand_less' : 'expand_more' }}</span>
      </button>
      <span v-else class="flex items-center">
        <span class="material-icons-round mr-1" aria-hidden="true">push_pin</span>
        <span class="fs-exclude">{{ heading }}</span>
      </span>
    </h2>
    <Transition
      enter-from-class="!grid-rows-[0fr]"
      leave-to-class="!grid-rows-[0fr]"
      :enter-active-class="COLLAPSE_TRANSITION"
      :leave-active-class="COLLAPSE_TRANSITION"
    >
      <div v-show="showNotes" class="grid grid-rows-[1fr]">
        <div class="min-h-0 text-text-2xl leading-8 text-gray-900 [&_a]:text-blue-400 [&_a]:underline">
          <WikiTextEditor
            v-if="editable"
            ref="editorRef"
            format="markdown"
            :wiki-text="editedNotes"
            :labelled-by="headingId"
            :label="heading"
            :place-holder="PLACEHOLDER"
            :reset-value="notes"
            show-reset
            show-copied-check
            :read-only="readOnly"
            :read-only-hint="READ_ONLY_HINT"
            @update:wiki-text="updateNotes"
          />
          <WikiText v-else class="fs-exclude" :text="notes" />
        </div>
      </div>
    </Transition>
  </section>
</template>
